// src/TickerDesktopFocus.js
import React from "react";
import { toPersianDigits } from "../../utils/toPersianDigits";
import { getTickerDuration } from "../../utils/tickerMotion";
import "./TickerDesktopFocus.css";

const getTickerItemKey = (item, index) =>
  item?.id ?? item?.link ?? `${index}-${item?.title || ""}`;

const TickerDesktopFocus = ({
  randomNews = [],
  paused = false,
  setPaused,
  onItemClick,
  lockPaused = false,
  isActive = true,
}) => {
  const containerRef = React.useRef(null);
  const groupRef = React.useRef(null);
  const contentRef = React.useRef(null);

  const [tickerLayout, setTickerLayout] = React.useState({
    copies: 3,
    distance: 0,
  });

  const tickerSignature = React.useMemo(
    () =>
      randomNews
        .map((item, index) => getTickerItemKey(item, index))
        .join("|"),
    [randomNews]
  );

  React.useLayoutEffect(() => {
    let isMounted = true;

    const updateLayout = () => {
      if (!isMounted) return;

      const containerWidth = containerRef.current?.offsetWidth || 0;
      const groupWidth = groupRef.current?.scrollWidth || 0;

      if (!containerWidth || !groupWidth) return;

      const measuredDistance = Math.ceil(groupWidth);
      const neededCopies = Math.min(
        12,
        Math.max(3, Math.ceil(containerWidth / measuredDistance) + 2)
      );

      setTickerLayout((current) =>
        current.copies === neededCopies &&
        current.distance === measuredDistance
          ? current
          : { copies: neededCopies, distance: measuredDistance }
      );
    };

    updateLayout();

    const rafId =
      typeof requestAnimationFrame === "function"
        ? requestAnimationFrame(updateLayout)
        : null;

    if (typeof document !== "undefined" && document.fonts?.ready) {
      document.fonts.ready.then(updateLayout).catch(() => {});
    }

    if (typeof ResizeObserver === "undefined") {
      if (typeof window !== "undefined") {
        window.addEventListener("resize", updateLayout);
      }

      return () => {
        isMounted = false;

        if (rafId !== null && typeof cancelAnimationFrame === "function") {
          cancelAnimationFrame(rafId);
        }

        if (typeof window !== "undefined") {
          window.removeEventListener("resize", updateLayout);
        }
      };
    }

    const observer = new ResizeObserver(updateLayout);

    if (containerRef.current) observer.observe(containerRef.current);
    if (groupRef.current) observer.observe(groupRef.current);

    return () => {
      isMounted = false;

      if (rafId !== null && typeof cancelAnimationFrame === "function") {
        cancelAnimationFrame(rafId);
      }

      observer.disconnect();
    };
  }, [tickerSignature]);

  const tickerGroups = Array.from(
    { length: tickerLayout.copies },
    (_, copyIndex) => copyIndex
  );

  return (
    <div
      ref={containerRef}
      className={`ticker-desktop-focus${
        isActive ? " ticker-desktop-focus--active" : ""
      }`}
      aria-hidden={!isActive}
    >
      <div
        ref={contentRef}
        className={`ticker-desktop-focus__content ${
          paused ? "ticker-desktop-focus__content--paused" : ""
        }`}
        style={{
          "--ticker-desktop-focus-distance": `${tickerLayout.distance}px`,
          "--ticker-desktop-focus-duration": getTickerDuration(
            tickerLayout.distance
          ),
        }}
      >
        {tickerGroups.map((copyIndex) => (
          <div
            key={copyIndex}
            ref={copyIndex === 0 ? groupRef : null}
            className="ticker-desktop-focus__group"
            aria-hidden={copyIndex > 0}
          >
            {randomNews.map((item, index) => (
              <button
                key={`${copyIndex}-${getTickerItemKey(item, index)}`}
                type="button"
                className="ticker-desktop-focus__item"
                tabIndex={isActive && copyIndex === 0 ? 0 : -1}
                onClick={(event) => onItemClick?.(item, event)}
              >
                <span
                  className="ticker-desktop-focus__title"
                  onMouseEnter={() => {
                    if (isActive) setPaused?.(true);
                  }}
                  onMouseLeave={() => {
                    if (isActive && !lockPaused) setPaused?.(false);
                  }}
                >
                  {toPersianDigits(item?.title)}
                </span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TickerDesktopFocus;